import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CalendarDays, ClipboardList, Factory, FileDown, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, exportFile } from "../lib/api";
import { Empty, Loading, PageHeader, Panel } from "../components/common";
import { Button } from "../components/ui/button";

const Tile = ({ to, icon: Icon, title, desc }) => (
  <Link to={to} className="panel panel-hover !p-5 flex items-start gap-4" data-testid={`maint-hub-${to.split("/").pop()}`}>
    <span className="w-10 h-10 rounded-xl bg-[var(--teal-soft)] flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[var(--teal)]" /></span>
    <span><span className="block font-semibold text-[var(--navy)]">{title}</span><span className="text-sm text-slate-500">{desc}</span></span>
  </Link>
);

export function ManutenzioniHub() {
  return (
    <div>
      <PageHeader subtitle="Impianti e interventi" title="Manutenzioni" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 stagger">
        <Tile to="/manutenzioni/impianti" icon={Factory} title="Impianti" desc="Aree, locali, impianti, attrezzature" />
        <Tile to="/manutenzioni/beni-mobili" icon={Truck} title="Beni mobili" desc="Mezzi: auto, battello, bici, pompe..." />
        <Tile to="/manutenzioni/scadenze" icon={CalendarClock} title="In scadenza" desc="Manutenzioni da programmare, per data" />
        <Tile to="/manutenzioni/interventi" icon={ClipboardList} title="Interventi" desc="Schede intervento" />
        <Tile to="/manutenzioni/calendario" icon={CalendarDays} title="Calendario" desc="Planning settimanale interventi" />
        <Tile to="/manutenzioni/export" icon={FileDown} title="Esporta storico (XLS)" desc="Beni mobili, Pontili/Darsene, Impianti, Cumulativo" />
      </div>
    </div>
  );
}

export function Calendario() {
  const [list, setList] = useState(null);
  const [off, setOff] = useState(0);
  useEffect(() => { api("/maint/schede").then((d) => setList(d.schede || [])).catch(() => setList([])); }, []);
  if (!list) return <Loading />;
  const parse = (v) => { if (!v) return null; const [d, m, y] = v.split("/"); return new Date(+y, +m - 1, +d); };
  const mon = new Date(); mon.setHours(0, 0, 0, 0); mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7) + off * 7);
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(mon); d.setDate(mon.getDate() + i); return d; });
  const on = (day) => list.filter((s) => { const a = parse(s.data_inizio), b = parse(s.data_fine) || a; return a && day >= a && day <= b; });
  return (
    <div>
      <PageHeader subtitle="Planning settimanale interventi" title="Calendario" actions={<><Button variant="outline" className="rounded-full" onClick={() => setOff(off - 1)} data-testid="cal-prev">‹</Button><Button variant="outline" className="rounded-full" onClick={() => setOff(0)} data-testid="cal-today">Oggi</Button><Button variant="outline" className="rounded-full" onClick={() => setOff(off + 1)} data-testid="cal-next">›</Button></>} />
      <div className="grid md:grid-cols-7 gap-3" data-testid="cal-week">{days.map((d, i) => (
        <div key={i} className={`panel !p-3 min-h-[160px] ${d.toDateString() === new Date().toDateString() ? "!border-[var(--teal)]" : ""}`}>
          <p className="text-xs uppercase tracking-widest text-slate-500">{["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"][i]}</p><p className="font-display text-xl font-bold text-[var(--navy)] mb-2">{d.getDate()}</p>
          {on(d).length === 0 ? <p className="text-xs text-slate-400">Nessun intervento</p> : on(d).map((s) => (
            <Link key={s.id} to="/manutenzioni/interventi" className={`block rounded-lg px-2 py-1.5 mb-1.5 text-xs ${s.stato === "Risolto" ? "bg-emerald-50 text-emerald-800" : "bg-[var(--teal-soft)] text-[var(--navy)]"}`}><b>n. {s.numero}</b> {s.titolo}<span className="block text-[10px] opacity-70">{s.impianto_label}</span></Link>))}
        </div>))}
      </div>
    </div>
  );
}

const STATI = { scaduto: ["Scaduti", "tag-red"], in_scadenza: ["In scadenza", "tag-amber"], ok: ["In regola", "tag-green"] };

export function Scadenze() {
  const [list, setList] = useState(null);
  useEffect(() => { api("/maint/impianti/scadenze").then((d) => setList(d.scadenze || [])).catch(() => setList([])); }, []);
  if (!list) return <Loading />;
  const group = (k) => list.filter((s) => (k === "ok" ? !["scaduto", "in_scadenza"].includes(s.stato_scadenza) : s.stato_scadenza === k));
  return (
    <div>
      <PageHeader subtitle="Impianti e mezzi, per data" title="In scadenza" />
      {list.length === 0 ? <Empty title="Nessuna manutenzione programmata" text="Imposta 'data ultima verifica' e 'programma (gg)' sugli impianti." /> : Object.entries(STATI).map(([k, [label, cls]]) => group(k).length > 0 && (
        <section key={k} className="mb-8"><h2 className="font-display text-lg font-bold text-[var(--navy)] mb-3">{label} ({group(k).length})</h2>
          <div className="grid sm:grid-cols-2 gap-3" data-testid={`scadenze-${k}`}>{group(k).map((s) => (
            <Panel key={s.id} className="!p-4"><div className="flex justify-between gap-2"><span className="font-semibold text-[var(--navy)]">{s.descrizione}</span><span className={`tag ${cls}`}>{s.due_it}</span></div>
              <p className="text-sm text-slate-500 mt-1">{s.area_label}</p>
              <p className="text-xs text-slate-400 mt-2">Ultima verifica {s.data_ultima_verifica || "—"}{s.programma_gg ? ` · ogni ${s.programma_gg} gg` : ""} · {s.giorni_mancanti < 0 ? `scaduto da ${-s.giorni_mancanti} gg` : `tra ${s.giorni_mancanti} gg`}</p></Panel>))}
          </div></section>))}
    </div>
  );
}

const EXPORTS = [["beni-mobili", "Beni mobili", "Storico manutenzioni mezzi"], ["pontili-darsene", "Pontili e Darsene", "Interventi posti barca e darsene"], ["impianti", "Impianti", "Schede intervento impianti"], ["cumulativo", "Cumulativo", "Tutte le manutenzioni in un unico file"]];

export function MaintExport() {
  const [busy, setBusy] = useState("");
  const run = async (k) => { setBusy(k); try { await exportFile(`/maint/export/${k}`); toast.success("Export pronto"); } catch { toast.error("Errore durante l'esportazione"); } finally { setBusy(""); } };
  return (
    <div>
      <PageHeader subtitle="Manutenzioni in formato XLS" title="Esporta storico" />
      <div className="grid sm:grid-cols-2 gap-3">{EXPORTS.map(([k, l, d]) => (
        <Panel key={k} className="!p-5 flex items-center justify-between gap-4"><div><p className="font-semibold text-[var(--navy)]">{l}</p><p className="text-sm text-slate-500">{d}</p></div>
          <Button variant="outline" className="rounded-full" disabled={!!busy} onClick={() => run(k)} data-testid={`maint-export-${k}`}><FileDown className="w-4 h-4 mr-1" />{busy === k ? "..." : "XLS"}</Button></Panel>))}
      </div>
    </div>
  );
}
